import React, { useContext, useState } from 'react';
import {
  Form,
  Card,
  Input,
  Button,
  message,
  Radio,
  Alert,
  Select,
  Tooltip,
  InputNumber,
  Checkbox,
  Popconfirm,
  DatePicker,
  TimePicker,
} from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import { QuestionCircleOutlined } from '@ant-design/icons';
import AppContext from '../../../components/AppContext';
import PageTitle from '../../../components/PageTitle';
import ProgressModal from '../../../components/ProgressModal';
import Loading from '../../../components/Loading';
import Error from '../../../components/Error';
import { COUNTRIES, GROUP_TYPE } from '../../../utils/constants';
import {
  QueryShippingGroupArgs,
  ShippingGroup,
  MutationAddShippingGroupArgs,
  MutationDeactivateShippingGroupArgs,
  MutationUpdateShippingGroupArgs,
  ShippingGroupStatus,
  ShippingGroupType,
} from '../../../utils/types';
import { explodeShippingRoutesByPrices, filterShippingRoutesForGroups, findShippingRoute, shortPriceDescription, useShippingRoutes } from '../../../utils/shippingRouteHelper';

const ADD_SHIPPING_GROUP_MUTATION = gql`
  mutation AddShippingGroupMutation($shippingGroup: AddShippingGroupInput!) {
    addShippingGroup(shippingGroup: $shippingGroup) {
      id
      createdAt
      zipCode
      city
      name
      deadline
      type
    }
  }
`;

const SHIPPING_GROUP = gql`
  {
    id
    createdAt
    updatedAt
    name
    zipCode
    city
    state
    country
    address
    recipientName
    phone
    notice
    type
    status
    deadline
    minWeight
    shippingRoute {
      id
      name
      country
      speed
      shippingMethod
      shipmentType
      dimensionalFactor
      isActive
      canGroup
      canDirect
      priceRanges {
          startWeight
          unitPrice
          basePrice
          roundingWeight
      }
      description
    }
    countdownSeconds
    hasFixedLeader
    isPrivate
    leader {
      id
      name
      code
    }
  }
`;

const SHIPPING_GROUP_QUERY = gql`
  query ShippingGroupQuery($id: ID!) {
    shippingGroup(id: $id) ${SHIPPING_GROUP}
  }
`;

const UPDATE_SHIPPING_GROUP_MUTATION = gql`
  mutation updateShippingGroupMutation($shippingGroup: ShippingGroupInput!, $id: ID!) {
    updateShippingGroup(shippingGroup: $shippingGroup, id: $id) ${SHIPPING_GROUP}
  }
`;

const DEACTIVATE_SHIPPING_GROUP_MUTATION = gql`
  mutation deactivateShippingGroupMutation($id: ID!) {
    deactivateShippingGroup(id: $id) ${SHIPPING_GROUP}
  }
`;

type Mode = 'add' | 'edit';

type FormType = {
  name: string,
  hasFixedLeader: boolean,
  isPrivate?: boolean,
  newPrivateCode?: string,
  type: ShippingGroupType,
  country: string,
  shippingServiceAndMinWeight: string,
  minWeight: number,
  countdownHours: number,
  recipientName?: string,
  phone?: string,
  address?: string,
  city: string,
  state: string,
  zipCode: string,
  deadline?: moment.Moment,
  notice: string,
};

function AddGroup(props: { mode: Mode }) {
  const { mode } = props;
  const { user } = useContext(AppContext);
  const [serviceNotes, setServiceNotes] = useState('');

  const { id } = useParams<{ id: string }>();

  const { loading, data = { shippingGroup: null } } = useQuery<
    { shippingGroup: ShippingGroup },
    QueryShippingGroupArgs
  >(SHIPPING_GROUP_QUERY, {
    variables: { id: id || '' },
    fetchPolicy: 'network-only',
    skip: mode === 'add',
  });

  const [addShippingGroup] = useMutation<
    { addShippingGroup: ShippingGroup },
    MutationAddShippingGroupArgs
  >(ADD_SHIPPING_GROUP_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();
      message.success('创建成功');
      history.replace('/groups');
    },
  });

  const [updateShippingGroup] = useMutation<
    { updateShippingGroup: ShippingGroup },
    MutationUpdateShippingGroupArgs
  >(UPDATE_SHIPPING_GROUP_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();
      message.success('修改成功');
      history.replace(`/groups/${data.updateShippingGroup.id}`);
    },
  });

  const [deactivateShippingGroup] = useMutation<
    { deactivateShippingGroup: ShippingGroup },
    MutationDeactivateShippingGroupArgs
  >(DEACTIVATE_SHIPPING_GROUP_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();
      message.success('已关闭');
      history.replace(`/groups`);
    },
  });

  const { loading: loadingShippingRoutes, data: shippingRoutesData = { shippingRoutes: null } } = useShippingRoutes();

  const onFinish = ({ countdownHours, shippingServiceAndMinWeight, hasFixedLeader, isPrivate, newPrivateCode, ...values }: FormType) => {
    const [shippingService] = shippingServiceAndMinWeight.split(':');
    const input = {
      countdownSeconds: countdownHours * 3600,
      shippingService,
      hasFixedLeader,
      ...(hasFixedLeader ? {
        address: values.address,
        recipientName: values.recipientName,
        phone: values.phone,
      }: {}),
      ...(!isPrivate ? {
        privateCode: '',
      }: newPrivateCode ? {
        privateCode: newPrivateCode,
      }: {}),

      name: values.name,
      type: values.type,
      country: values.country,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      deadline: values.deadline,
      notice: values.notice,
    };

    ProgressModal.show();
    if (mode === 'add') {
      const addInput = {
        ...input,
        country: values.country,
        shippingService,
        minWeight: values.minWeight,
      }

      addShippingGroup({ variables: { shippingGroup: addInput } });
    } else {
      updateShippingGroup({ variables: { shippingGroup: input, id: id || '' } });
    }
  };

  const deactivateClicked = () => {
    deactivateShippingGroup({ variables: { id: id || '' } });
  };

  const history = useHistory();
  const [form] = Form.useForm();

  const { shippingRoutes } = shippingRoutesData;

  const changeShippingService = value => {
    const route = findShippingRoute(shippingRoutesData.shippingRoutes || [], value);
    setServiceNotes(route?.notes || '');
  };

  const resetShippingService = () => {
    form.setFieldsValue({ shippingService: '' });
    setServiceNotes('');
  };

  const shippingGroup = data.shippingGroup;
  const initialValues =
    mode === 'add'
      ? { type: 'REGULAR', hasFixedLeader: false }
      : {
          ...shippingGroup,
          shippingService: shippingGroup?.shippingRoute?.id,
          shippingServiceAndMinWeight: `${shippingGroup?.shippingRoute?.id}:${shippingGroup?.minWeight}`,
          deadline: shippingGroup?.deadline ? moment(shippingGroup.deadline) : null,
          countdownHours: shippingGroup?.countdownSeconds
            ? shippingGroup.countdownSeconds / 3600
            : undefined,
        };

  const editAndShippingGroupClosed =
    mode === 'edit' && shippingGroup?.status !== ShippingGroupStatus.Open;

  const disabledDate = (current) => {
    // Can not select days before today
    return current && current < moment().startOf('day');
  }

  const disabledHours = () => {
    // Can not select past time
    if (moment().isSame(moment(form.getFieldValue('deadline')), 'day')) {
      return Array.from(Array(24).keys()).filter(hour => hour < moment().hour());
    }
    return [];
  }

  const disabledMinutes = (selectedHour) => {
    // Can not select past time
    if (selectedHour === moment().hour() && moment().isSame(moment(form.getFieldValue('deadline')), 'day')) {
      return Array.from(Array(60).keys()).filter(minute => minute < moment().minute());
    }
    return [];
  }

  return (mode === 'edit' && loading) || loadingShippingRoutes ? (
    <Loading />
  ) : (mode === 'edit' && !shippingGroup) || !shippingRoutes ? (
    <Error />
  ) : mode === 'edit' && shippingGroup?.leader?.id !== user.id ? (
    <Error message="你不是团长！" />
  ) : (
    <div className="AddGroup">
      <PageTitle>{mode === 'add' ? '创建拼邮团' : '管理拼邮团'}</PageTitle>
      <Card bordered={false}>
        <Form onFinish={onFinish} initialValues={initialValues} form={form}>
          <Form.Item
            label="名称（如: Sunnyvale普货团，不要加月份）"
            name="name"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input allowClear disabled={editAndShippingGroupClosed} />
          </Form.Item>
          {!editAndShippingGroupClosed && (
            <>
              <Form.Item>
                <Form.Item
                  name="hasFixedLeader"
                  valuePropName="checked"
                  style={{ marginBottom: '5px' }}
                >
                  <Checkbox>固定团长</Checkbox>
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) =>
                    getFieldValue('hasFixedLeader') ? (
                      <Alert
                        type="warning"
                        message="选择成为固定团长的话，每次截团，包裹都会寄到固定团长家"
                      />
                    ) : (
                      <Alert
                        type="warning"
                        message="创建人将自动成为临时团长，截团后总重量最大的人自动成为收货团长，包裹最终会寄到收货团长家"
                      />
                    )
                  }
                </Form.Item>
              </Form.Item>

              <Form.Item>
                <Form.Item name="isPrivate" valuePropName="checked" style={{ marginBottom: '5px' }}>
                  <Checkbox>{'🔒 '}私人团</Checkbox>
                </Form.Item>
                <Alert
                  type="warning"
                  message="设为私人团后，添加包裹需要参团口令，口令正确才能加入"
                />
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) =>
                  getFieldValue('isPrivate') && (
                    <Form.Item
                      label={
                        mode === 'edit' && shippingGroup?.isPrivate
                          ? '更改参团口令（如不需修改请留空）'
                          : '参团口令'
                      }
                      name="newPrivateCode"
                      rules={[
                        {
                          required: !(mode === 'edit' && shippingGroup?.isPrivate),
                          message: '必填',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  )
                }
              </Form.Item>

              <Form.Item label="类型" name="type" rules={[{ required: true, message: '必填' }]}>
                <Radio.Group onChange={resetShippingService} disabled={mode !== 'add'}>
                  {Object.entries(GROUP_TYPE).map(([type, value]) =>
                    <Radio value={type} key={type}>{value.text}</Radio>
                  )}
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label="所在国家"
                name="country"
                rules={[{ required: true, message: '必填' }]}
              >
                <Select onChange={resetShippingService} disabled={mode !== 'add'}>
                  {Object.entries(COUNTRIES).map(([countryCode, countryName]) => (
                    <Select.Option value={countryCode} key={countryCode}>
                      {countryName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  return (
                    getFieldValue('country') && (
                      <Form.Item
                        label="线路渠道"
                        name="shippingServiceAndMinWeight"
                        required
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Select
                          onChange={(value) => {
                            if (value) {
                              const [shippingService, minWeight] = (value as string).split(':');
                              form.setFieldsValue({ shippingService, minWeight: +minWeight });
                              changeShippingService(shippingService);
                            }
                          }}
                          disabled={mode !== 'add'}
                          value={getFieldValue('shippingService') ? `${getFieldValue('shippingService')}:${getFieldValue('minWeight')}` : undefined}
                        >
                          {explodeShippingRoutesByPrices(filterShippingRoutesForGroups(shippingRoutes, form.getFieldValue('country'), form.getFieldValue('type')))
                            .map(({ route, weight, priceRange }, index) => (
                              <Select.Option value={`${route.id}:${weight}`} key={index}>
                                {route.name} {weight}kg {shortPriceDescription(priceRange)} {route.speed}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>
                    )
                  );
                }}
              </Form.Item>

              {serviceNotes && <Alert type="warning" message={serviceNotes} />}

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  return (
                    getFieldValue('shippingService') && (
                      <Form.Item
                        label="最低成团重量(kg)"
                        name="minWeight"
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <InputNumber disabled />
                      </Form.Item>
                    )
                  );
                }}
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  return (
                    getFieldValue('shippingService') && (
                      <Form.Item
                        label={
                          <span>
                            凑满重量后截团倒计时(小时)&nbsp;
                            <Tooltip title="凑满重量后不会马上截团，会自动开启截团倒计时，倒计时期间仍然可以跟团。但倒计时结束后再入库的包裹都无法跟团发走，会被系统自动添加到新开的同名团，后续可以修改包裹加到别的团。填0表示到达重量后马上截团(不建议，打包完可能不够重量)。截团已经进入倒计时后再改倒计时无效">
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </span>
                        }
                        name="countdownHours"
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <InputNumber
                          min={0}
                          max={168}
                          placeholder="0~168"
                          disabled={shippingGroup?.deadline}
                        />
                      </Form.Item>
                    )
                  );
                }}
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) =>
                  getFieldValue('hasFixedLeader') && (
                    <>
                      <Alert type="warning" message="固定团长请准确填写收货地址" />
                      <Form.Item
                        label="收货人姓名"
                        name="recipientName"
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input allowClear />
                      </Form.Item>
                      <Form.Item
                        label="收货人电话"
                        name="phone"
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input allowClear />
                      </Form.Item>
                      <Form.Item
                        label="街道地址"
                        name="address"
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input allowClear />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>

              <Form.Item label="所在城市" name="city" rules={[{ required: true, message: '必填' }]}>
                <Input allowClear />
              </Form.Item>

              <Form.Item label="所在州" name="state" rules={[{ required: true, message: '必填' }]}>
                <Input allowClear />
              </Form.Item>

              <Form.Item
                label="所在地邮编"
                name="zipCode"
                rules={[
                  { required: true, message: '必填' },
                  // { type: 'string', pattern: /^[0-9]+$/, len: 5, message: '邮编不合法'},
                ]}
              >
                <Input allowClear />
              </Form.Item>
              {mode === 'edit' && shippingGroup?.status === 'OPEN' && shippingGroup.deadline && (
                <Form.Item label="截团时间（你的本地时间）" required>
                  <>
                    <Form.Item
                      name="deadline"
                      noStyle
                      rules={[{ required: true, message: '必填' }]}
                    >
                      <DatePicker
                        showToday={false}
                        disabledDate={disabledDate}
                        allowClear
                        placeholder="截团日期"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="deadline"
                      noStyle
                      rules={[{ required: true, message: '必填' }]}
                    >
                      <TimePicker
                        allowClear
                        format="HH:mm"
                        placeholder="截团时间"
                        style={{ width: '100%', marginTop: '10px' }}
                        showNow={false}
                        hideDisabledOptions
                        disabledHours={disabledHours}
                        disabledMinutes={disabledMinutes}
                        minuteStep={5}
                      />
                    </Form.Item>
                    {/* <Popconfirm
                      key="delete"
                      title="确认要马上截团吗？截团后不可恢复。"
                      // onConfirm={() => onCloseGroup(shippingGroup.id)}
                      okText="确定"
                      cancelText="再等等"
                    >
                      <Button size="small" type="primary" style={{ marginLeft: '1rem' }}>
                        马上截团
                      </Button>
                    </Popconfirm> */}
                  </>
                </Form.Item>
              )}
            </>
          )}

          <Form.Item
            label="团公告"
            name="notice"
            // rules={[{ }]}
          >
            <Input.TextArea allowClear placeholder="价格、参团说明、取货说明等" />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit">
              {mode === 'add' ? '创建' : '修改'}
            </Button>
          </Form.Item>

          {mode === 'edit' && shippingGroup?.status === ShippingGroupStatus.Open && (
            <Form.Item>
              <Popconfirm
                key="delete"
                title="确认要关闭拼邮团吗？关闭后将不再显示在拼邮团列表中"
                onConfirm={() => deactivateClicked()}
                okText="确认关闭"
                cancelText="不关闭"
              >
                <Button block type="primary" ghost>
                  关闭拼邮团
                </Button>
              </Popconfirm>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
}

export default AddGroup;
